/**
 * The live voice connection — one `Room` for the whole app.
 *
 * Mounted at Layout level, ABOVE the router, on purpose: a provider inside the
 * conversation route would be unmounted the moment you click another
 * conversation, tearing down the WebRTC connection and dropping you out of the
 * call. Discord keeps you in voice while you wander; so does this.
 *
 * The media path is browser <-> LiveKit. Our backend only hands out the join
 * token (`requestVoiceToken`), so a backend restart — every deploy does one —
 * cannot end a call in progress.
 *
 * Remote audio is attached as real `<audio>` elements inside a hidden
 * container. Browsers block autoplay until the user has interacted; the click
 * on "Join" is that interaction, which is why joining is always explicit.
 */
import {
  ConnectionState,
  Room,
  RoomEvent,
  Track,
  type LocalTrackPublication,
  type Participant,
  type RemoteTrack,
  type RemoteTrackPublication,
} from 'livekit-client';
import { isSoundboardId, playSoundboardSound, type SoundboardId } from '../lib/soundboard';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { requestVoiceToken } from '../lib/voiceApi';
import { joinVoiceCall, leaveVoiceCall } from '../lib/voiceCallsApi';

/** How many times the call-record join is tried before this person's transcript is given up on. */
const JOIN_RECORD_ATTEMPTS = 4;
/** Base wait between attempts; grows linearly (1.5 s, 3 s, 4.5 s). */
const JOIN_RECORD_RETRY_MS = 1_500;
import { playJoinCue, playLeaveCue, playShareStartCue, playShareStopCue } from '../lib/voiceSounds';
import {
  canTranscribeLocally,
  startMicTranscriber,
  type MicTranscriberHandle,
  type TranscriberStopReason,
} from '../lib/voiceTranscriber';

/** One person in the room, as the UI needs them. */
export interface VoiceMember {
  userId: string;
  name: string;
  muted: boolean;
  sharingScreen: boolean;
  isLocal: boolean;
}

export type VoiceStatus = 'idle' | 'connecting' | 'reconnecting' | 'connected';

/**
 * One screen being shared. There can be several at once: a designer sharing a
 * mock while someone else shares the build log is ordinary, and the previous
 * single-slot model silently deleted the first share when the second arrived.
 */
export interface VoiceScreenShare {
  /** The video track, for the stage to attach to its own <video>. */
  track: Track;
  /** Identity of whoever published it — used to prune on a hard disconnect. */
  publisherId: string;
  publisherName: string;
  /** Our own share: never rendered, or the capture would tunnel infinitely. */
  isLocal: boolean;
}

/**
 * `getDisplayMedia` simply does not exist on iOS/iPadOS — every browser there
 * is WebKit, and screen capture is an OS function, not a web API. Feature
 * detection, not browser sniffing: the button is absent where it cannot work
 * and present everywhere it can.
 */
export function canShareScreen(): boolean {
  return typeof navigator?.mediaDevices?.getDisplayMedia === 'function';
}

/** i18n keys, not sentences — the provider must not know about a locale. */
export type VoiceErrorKey =
  | 'teamChat.voiceErrorMicDenied'
  | 'teamChat.voiceErrorUnavailable'
  | 'teamChat.voiceErrorGeneric';

/**
 * Why our own transcript stopped while the call went on, for the UI to say so.
 * Null while it runs, or when it never started because the server has it off.
 */
export type TranscriptStopKey =
  | 'teamChat.voiceTranscriptBudget'
  | 'teamChat.voiceTranscriptUnsupported'
  | 'teamChat.voiceTranscriptStopped';

interface VoiceRoomContextValue {
  /** Which voice channel we're in (or joining); null when idle. */
  channelId: string | null;
  /**
   * The history record of this call, once the server has opened it. Null
   * while connecting and when the record could not be created — a failed
   * record never blocks the call itself.
   */
  callId: string | null;
  /** Server has transcription on for calls (needs an OpenAI key). */
  transcriptionEnabled: boolean;
  /** Our microphone is being recorded and transcribed right now. */
  transcribing: boolean;
  transcriptStopped: TranscriptStopKey | null;
  /**
   * Its name, supplied by whoever called `join`. Held here so the dock can
   * label the call without fetching a list just to turn an id into a string.
   */
  channelName: string;
  status: VoiceStatus;
  members: VoiceMember[];
  /** Identities talking right now, for the speaking ring. */
  speakingIds: string[];
  micEnabled: boolean;
  /** Whether WE are the one sharing — drives the button's pressed state. */
  screenShareEnabled: boolean;
  /** Every screen on the stage right now, ours included. */
  screenShares: VoiceScreenShare[];
  error: VoiceErrorKey | null;
  join: (channelId: string, channelName: string) => Promise<void>;
  leave: () => Promise<void>;
  toggleMic: () => Promise<void>;
  toggleScreenShare: () => Promise<void>;
  /** Fire a soundboard sound for everyone in the room, ourselves included. */
  playSoundboard: (id: SoundboardId) => void;
  dismissError: () => void;
}

/** LiveKit data topic for soundboard presses. */
const SOUNDBOARD_TOPIC = 'soundboard';

/**
 * One sound per this many ms, per person. Someone leaning on the air horn is
 * funny for about two seconds and then it is just a denial of service on
 * everyone's ears.
 */
const SOUNDBOARD_COOLDOWN_MS = 900;

const VoiceRoomContext = createContext<VoiceRoomContextValue | undefined>(undefined);

function toMember(participant: Participant, isLocal: boolean): VoiceMember {
  return {
    userId: participant.identity,
    name: participant.name || participant.identity,
    muted: !participant.isMicrophoneEnabled,
    sharingScreen: participant.isScreenShareEnabled,
    isLocal,
  };
}

/** Which error the UI should show, without leaking SDK exception shapes. */
function classifyError(err: unknown): VoiceErrorKey {
  const name = err instanceof Error ? err.name : '';
  if (name === 'NotAllowedError' || name === 'SecurityError') return 'teamChat.voiceErrorMicDenied';
  const status = (err as { status?: number } | null)?.status;
  if (status === 503) return 'teamChat.voiceErrorUnavailable';
  return 'teamChat.voiceErrorGeneric';
}

export function VoiceRoomProvider({ children }: { children: React.ReactNode }) {
  const roomRef = useRef<Room | null>(null);
  // Mirrors `channelId` in a ref so `join` can compare against it without the
  // state value in its closure: a double-click lands both calls in the same
  // render, and the second one would otherwise tear down the first one's
  // in-flight connection.
  const activeIdRef = useRef<string | null>(null);
  const audioRef = useRef<HTMLDivElement | null>(null);
  // The call record and our recorder, in refs: teardown runs from SDK events
  // and from unmount, where state would be stale or already gone.
  const callIdRef = useRef<string | null>(null);
  const transcriberRef = useRef<MicTranscriberHandle | null>(null);
  const [channelId, setChannelId] = useState<string | null>(null);
  const [callId, setCallId] = useState<string | null>(null);
  const [transcriptionEnabled, setTranscriptionEnabled] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [transcriptStopped, setTranscriptStopped] = useState<TranscriptStopKey | null>(null);
  const [channelName, setChannelName] = useState('');
  const [status, setStatus] = useState<VoiceStatus>('idle');
  const [members, setMembers] = useState<VoiceMember[]>([]);
  const [speakingIds, setSpeakingIds] = useState<string[]>([]);
  const [micEnabled, setMicEnabled] = useState(true);
  const [screenShareEnabled, setScreenShareEnabled] = useState(false);
  const [screenShares, setScreenShares] = useState<VoiceScreenShare[]>([]);
  const [error, setError] = useState<VoiceErrorKey | null>(null);

  /**
   * Rebuild the roster from the room. Cheaper and far less error-prone than
   * patching a list per event: every LiveKit event that can change who's in the
   * room or what they're publishing just calls this.
   */
  const syncMembers = useCallback(() => {
    const room = roomRef.current;
    if (!room) {
      setMembers([]);
      return;
    }
    const remote = [...room.remoteParticipants.values()].map((p) => toMember(p, false));
    setMembers([toMember(room.localParticipant, true), ...remote]);
    setMicEnabled(room.localParticipant.isMicrophoneEnabled);
    setScreenShareEnabled(room.localParticipant.isScreenShareEnabled);
  }, []);

  /**
   * Put a screen on the stage, or take one off. Everything is keyed by track:
   * adding dedupes on it (LiveKit can re-fire `TrackSubscribed` after a
   * resubscribe), and removing only drops the matching one, so a stale
   * "unpublished" cannot blank a screen someone else started meanwhile.
   */
  const stageShare = useCallback((next: VoiceScreenShare) => {
    setScreenShares((current) =>
      current.some((s) => s.track === next.track) ? current : [...current, next],
    );
  }, []);
  const clearShare = useCallback((track: Track) => {
    setScreenShares((current) =>
      current.some((s) => s.track === track) ? current.filter((s) => s.track !== track) : current,
    );
  }, []);
  /**
   * Someone's connection died. A hard drop emits no `TrackUnpublished`, so
   * without this their frozen last frame would sit on the stage forever.
   */
  const clearSharesOf = useCallback((publisherId: string) => {
    setScreenShares((current) =>
      current.some((s) => s.publisherId === publisherId)
        ? current.filter((s) => s.publisherId !== publisherId)
        : current,
    );
  }, []);

  /**
   * Close our side of the call record: flush the last chunk of audio, then
   * tell the server we left. Idempotent — the SDK's Disconnected fires after
   * our own leave() too — and never awaited by the caller: a slow upload must
   * not delay hanging up.
   */
  const endCallRecord = useCallback(() => {
    const id = callIdRef.current;
    const transcriber = transcriberRef.current;
    callIdRef.current = null;
    transcriberRef.current = null;
    setCallId(null);
    setTranscribing(false);
    setTranscriptStopped(null);
    if (!id) return;
    void (async () => {
      if (transcriber) await transcriber.stop().catch(() => undefined);
      await leaveVoiceCall(id).catch(() => undefined);
    })();
  }, []);

  // Closing the tab or the window never reaches leave(): say it on the way out,
  // or the history keeps the call "live" with nobody in it until the sweep.
  useEffect(() => {
    const onPageHide = () => {
      const id = callIdRef.current;
      if (id) void leaveVoiceCall(id, { keepalive: true }).catch(() => undefined);
    };
    window.addEventListener('pagehide', onPageHide);
    return () => window.removeEventListener('pagehide', onPageHide);
  }, []);

  const teardown = useCallback(() => {
    endCallRecord();
    roomRef.current = null;
    activeIdRef.current = null;
    setChannelId(null);
    setChannelName('');
    setStatus('idle');
    setMembers([]);
    setSpeakingIds([]);
    setScreenShares([]);
    setScreenShareEnabled(false);
  }, [endCallRecord]);

  /**
   * Open the history record for this call and start transcribing our own
   * microphone. Best-effort throughout: the call is LiveKit's and is already
   * up by the time this runs, so nothing here may throw back into join().
   *
   * Two things this must survive, because each one silently left a person
   * out of the transcript: a join request lost to a flaky network right after
   * connecting (so it is retried a few times), and the server closing the
   * call record under us — the stale sweep after a laptop sleep, or the last
   * other person leaving and coming back — while we are still in the room
   * (so on "call ended" we join again and file chunks under the new record).
   */
  const openCallRecord = useCallback(async (room: Room, target: string, attempt = 0) => {
    const stillHere = () => roomRef.current === room && activeIdRef.current === target;
    let record: { callId: string; transcriptionEnabled: boolean };
    try {
      record = await joinVoiceCall(target);
    } catch (err) {
      console.warn('[voice] call record join failed', err);
      if (attempt >= JOIN_RECORD_ATTEMPTS - 1) return;
      await new Promise((resolve) => setTimeout(resolve, JOIN_RECORD_RETRY_MS * (attempt + 1)));
      if (stillHere()) await openCallRecord(room, target, attempt + 1);
      return;
    }
    // The user hung up (or hopped channels) while the request was in flight.
    if (!stillHere()) {
      await leaveVoiceCall(record.callId).catch(() => undefined);
      return;
    }
    callIdRef.current = record.callId;
    setCallId(record.callId);
    setTranscriptionEnabled(record.transcriptionEnabled);
    if (!record.transcriptionEnabled) return;
    if (!canTranscribeLocally()) {
      setTranscriptStopped('teamChat.voiceTranscriptUnsupported');
      return;
    }
    const onStopped = (reason: TranscriberStopReason) => {
      transcriberRef.current = null;
      setTranscribing(false);
      if (reason === 'budget') setTranscriptStopped('teamChat.voiceTranscriptBudget');
      else if (reason === 'unsupported')
        setTranscriptStopped('teamChat.voiceTranscriptUnsupported');
      else if (reason === 'call-ended') {
        // The record closed but we are still talking: open a fresh one and
        // keep going, so the rest of the call is not lost.
        if (stillHere() && callIdRef.current === record.callId) {
          callIdRef.current = null;
          void openCallRecord(room, target);
        }
      } else setTranscriptStopped('teamChat.voiceTranscriptStopped');
    };
    transcriberRef.current = startMicTranscriber({ room, callId: record.callId, onStopped });
    setTranscribing(true);
    setTranscriptStopped(null);
  }, []);

  const leave = useCallback(async () => {
    const room = roomRef.current;
    teardown();
    if (room) {
      playLeaveCue();
      await room.disconnect();
    }
  }, [teardown]);

  const join = useCallback(
    async (target: string, targetName: string) => {
      // Re-clicking the channel you're already in is a no-op, not a reconnect.
      if (activeIdRef.current === target) return;
      if (roomRef.current) await leave();

      setError(null);
      activeIdRef.current = target;
      setChannelId(target);
      setChannelName(targetName);
      setStatus('connecting');

      // `adaptiveStream` is off deliberately. It pauses any track whose <video>
      // is not attached and visible, which is exactly the state a screen share
      // is in while you're reading another conversation — you'd come back to a
      // black rectangle. With a handful of people and one or two shares the
      // bandwidth it would have saved is not worth that. `dynacast` still
      // stops publishing layers nobody subscribes to, which is free.
      const room = new Room({ adaptiveStream: false, dynacast: true });
      roomRef.current = room;

      room
        .on(RoomEvent.ParticipantConnected, () => {
          playJoinCue();
          syncMembers();
        })
        .on(RoomEvent.ParticipantDisconnected, (participant: Participant) => {
          // A sharer whose connection died takes their screen with them; the
          // leave chime covers it, a second cue on top would just be noise.
          clearSharesOf(participant.identity);
          playLeaveCue();
          syncMembers();
        })
        .on(RoomEvent.TrackMuted, syncMembers)
        .on(RoomEvent.TrackUnmuted, syncMembers)
        .on(RoomEvent.LocalTrackPublished, (pub: LocalTrackPublication) => {
          // Our own share goes on the stage as a marker only — see isLocal.
          if (pub.source === Track.Source.ScreenShare && pub.track) {
            stageShare({
              track: pub.track,
              publisherId: room.localParticipant.identity,
              publisherName: '',
              isLocal: true,
            });
            playShareStartCue();
          }
          syncMembers();
        })
        .on(RoomEvent.LocalTrackUnpublished, (pub: LocalTrackPublication) => {
          if (pub.track) clearShare(pub.track);
          if (pub.source === Track.Source.ScreenShare) playShareStopCue();
          syncMembers();
        })
        .on(RoomEvent.TrackPublished, syncMembers)
        .on(RoomEvent.TrackUnpublished, syncMembers)
        .on(RoomEvent.ActiveSpeakersChanged, (speakers: Participant[]) =>
          setSpeakingIds(speakers.map((s) => s.identity)),
        )
        // Someone pressed a soundboard button: the payload is only the sound's
        // name, and each client synthesises it locally. Anything we don't
        // recognise is ignored rather than trusted — a data packet is input.
        .on(RoomEvent.DataReceived, (payload: Uint8Array, _p, _kind, topic?: string) => {
          if (topic !== SOUNDBOARD_TOPIC) return;
          try {
            const id = new TextDecoder().decode(payload);
            if (isSoundboardId(id)) playSoundboardSound(id);
          } catch {
            /* malformed packet — nothing to play */
          }
        })
        .on(
          RoomEvent.TrackSubscribed,
          (track: RemoteTrack, pub: RemoteTrackPublication, participant: Participant) => {
            if (track.kind === Track.Kind.Audio) {
              audioRef.current?.appendChild(track.attach());
            } else if (pub.source === Track.Source.ScreenShare) {
              stageShare({
                track,
                publisherId: participant.identity,
                publisherName: participant.name || participant.identity,
                isLocal: false,
              });
              playShareStartCue();
            }
            syncMembers();
          },
        )
        .on(RoomEvent.TrackUnsubscribed, (track: RemoteTrack, pub: RemoteTrackPublication) => {
          if (track.kind === Track.Kind.Audio) {
            track.detach().forEach((el) => el.remove());
          } else {
            clearShare(track);
            // Only when the screen is actually being shown on this side, so a
            // sharer who vanished with their connection (already cleared by
            // ParticipantDisconnected) does not chime twice.
            if (pub.source === Track.Source.ScreenShare) playShareStopCue();
          }
          syncMembers();
        })
        .on(RoomEvent.ConnectionStateChanged, (state: ConnectionState) => {
          // Reconnecting is neither connected nor disconnected. The SDK retries
          // on its own, so the dock must keep showing the call instead of
          // flickering to idle — but media is down meanwhile, and a green
          // "Connected" over a silent line is exactly what makes people decide
          // the feature is broken. This is the common case on a phone that
          // changed network or came back from a locked screen.
          //
          // `SignalReconnecting` is deliberately not included: only the
          // signalling socket is re-establishing there, audio keeps flowing,
          // and flagging it would cry wolf on every brief blip.
          if (state === ConnectionState.Connected) setStatus('connected');
          else if (state === ConnectionState.Reconnecting) setStatus('reconnecting');
        })
        .on(RoomEvent.Disconnected, () => {
          // Fires for a kick, a network death, or our own leave() — in every
          // case the call is over and the UI must not claim otherwise.
          if (roomRef.current === room) teardown();
        })
        .on(RoomEvent.MediaDevicesError, (err: Error) => setError(classifyError(err)));

      try {
        const grant = await requestVoiceToken(target);
        await room.connect(grant.url, grant.token);
        // Only after connecting, so a denied microphone leaves you connected
        // and listening rather than bouncing you out of the channel.
        await room.localParticipant.setMicrophoneEnabled(true);
      } catch (err) {
        setError(classifyError(err));
        // A failed mic still leaves a usable (muted) connection; a failed
        // connect does not, and `room.state` is how we tell them apart.
        if (room.state !== ConnectionState.Connected) {
          await room.disconnect().catch(() => undefined);
          if (roomRef.current === room) teardown();
          return;
        }
      }
      setStatus('connected');
      syncMembers();
      playJoinCue();
      // After the media, never before: the record exists to remember a call
      // that happened, and a join that failed at LiveKit is not one.
      void openCallRecord(room, target);
    },
    [clearShare, clearSharesOf, leave, openCallRecord, stageShare, syncMembers, teardown],
  );

  const toggleMic = useCallback(async () => {
    const room = roomRef.current;
    if (!room) return;
    const next = !room.localParticipant.isMicrophoneEnabled;
    // Optimistic: the SDK round-trips to the server, and a mute button that
    // waits for that feels broken.
    setMicEnabled(next);
    try {
      await room.localParticipant.setMicrophoneEnabled(next);
    } catch (err) {
      setError(classifyError(err));
    }
    syncMembers();
  }, [syncMembers]);

  const toggleScreenShare = useCallback(async () => {
    const room = roomRef.current;
    if (!room) return;
    const next = !room.localParticipant.isScreenShareEnabled;
    try {
      // Screen audio too — a shared browser tab with sound is the common case,
      // and the server grant already allows the screen_share_audio source.
      await room.localParticipant.setScreenShareEnabled(next, { audio: true });
    } catch (err) {
      // Dismissing the OS picker throws NotAllowedError. That is a decision,
      // not a failure, and must not put a red strip on the user's screen.
      if (!(err instanceof Error && err.name === 'NotAllowedError')) {
        setError(classifyError(err));
      }
    }
    syncMembers();
  }, [syncMembers]);

  const dismissError = useCallback(() => setError(null), []);

  // Closing the tab or logging out must not leave a ghost in the channel.
  useEffect(() => {
    return () => {
      endCallRecord();
      void roomRef.current?.disconnect();
      roomRef.current = null;
    };
  }, [endCallRecord]);

  const lastSoundAtRef = useRef(0);

  /**
   * Play it here and tell the room to play it too. We do not mix the sound
   * into the microphone track: that would be swallowed by echo cancellation,
   * would vanish while muted, and would reach nobody if the mic were denied.
   */
  const playSoundboard = useCallback((id: SoundboardId) => {
    const now = Date.now();
    if (now - lastSoundAtRef.current < SOUNDBOARD_COOLDOWN_MS) return;
    lastSoundAtRef.current = now;
    playSoundboardSound(id);
    const room = roomRef.current;
    if (!room || room.state !== ConnectionState.Connected) return;
    void room.localParticipant
      .publishData(new TextEncoder().encode(id), { reliable: true, topic: SOUNDBOARD_TOPIC })
      .catch(() => undefined);
  }, []);

  const value = useMemo(
    () => ({
      channelId,
      callId,
      transcriptionEnabled,
      transcribing,
      transcriptStopped,
      channelName,
      status,
      members,
      speakingIds,
      micEnabled,
      screenShareEnabled,
      screenShares,
      error,
      join,
      leave,
      toggleMic,
      toggleScreenShare,
      playSoundboard,
      dismissError,
    }),
    [
      channelId,
      callId,
      transcriptionEnabled,
      transcribing,
      transcriptStopped,
      channelName,
      status,
      members,
      speakingIds,
      micEnabled,
      screenShareEnabled,
      screenShares,
      error,
      join,
      leave,
      toggleMic,
      toggleScreenShare,
      playSoundboard,
      dismissError,
    ],
  );

  return (
    <VoiceRoomContext.Provider value={value}>
      {children}
      {/* Remote audio lives outside the React tree on purpose: the elements are
          created by the SDK's `track.attach()`, and re-rendering them would
          restart playback. `hidden` would stop playback in some browsers, so
          the container is merely invisible. */}
      <div ref={audioRef} aria-hidden className="sr-only" />
    </VoiceRoomContext.Provider>
  );
}

export function useVoiceRoom(): VoiceRoomContextValue {
  const ctx = useContext(VoiceRoomContext);
  if (!ctx) throw new Error('useVoiceRoom must be used inside a VoiceRoomProvider');
  return ctx;
}
